import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import RouterLink from './RouterLink';
import { UserCheck, ArrowRight } from 'lucide-react';
import { slotStatsApi, followBackApi } from '../../lib/api';
import { r14ErrorText } from '../../lib/r14Errors';
import {
  normaliseFollowBack, headline, sourceRows, bestOf, targetRanking, trendRows, heroSpark, normaliseBatches, batchOfSlot, pendingOfSlot, slotNumber,
  fmtRatePct, FB_TAG, classifyFbError,
} from '../../lib/followBackView';
import { Segmented } from './ChartKit';
import { CompactLine } from './R15Parts';
import {
  SignalStrip, KpiRow, TrendCard, EmptyNotice, NoSignalNote, BatchLink, StuckAlert, rangeOptions, fbLog,
} from './FollowBackParts';

/*
 * r16m (2026-10-20), design 3.7 "Per slot": the Follow-back card of Slot Stats, right after "Actions-done ratios" (under the page's
 * main block when /stats is empty), Instagram slots only (SlotHistoryPanels draws it with the other Instagram cards).
 *
 *   - its own query: GET /uiapi/slots/{slot}/follow-back?range=<the page's 7 / 30 / 90> or its own "All time";
 *   - the headline is the MATURED cohort with its dates; the last 7 days are their own "still maturing" tile and the dashed tail of
 *     the trend - never the headline;
 *   - an older server (no route) draws nothing and logs one console line; the part switched off on the server: one compact line;
 *   - a member of a batch gets a "Batch N · 24 slots" link to the group page.
 * Every rate is computed in lib/followBackView.js from the server's counts.
 */

export default function FollowBackCard({ slotName, range = 30 }) {
  const [allTime, setAllTime] = useState(false);
  const effective = allTime ? 'all' : range;
  const q = useQuery({
    queryKey: ['followBack', 'slot', slotName, effective],
    queryFn: () => slotStatsApi.followBack(slotName, { range: effective }).then((r) => r.data),
    retry: false,
    // a range change keeps the previous figures on screen (faded) until the new ones arrive: no skeleton, no layout jump
    placeholderData: (prev) => prev,
  });
  const m = useMemo(() => (q.data ? normaliseFollowBack(q.data) : null), [q.data]);
  // the batch link: from the answer's scope.batch, else from the batch records (only asked when the answer has none)
  const needBatches = !!m && !m.batch;
  const bq = useQuery({
    queryKey: ['followBackBatches'],
    queryFn: () => followBackApi.batches().then((r) => r.data),
    enabled: needBatches,
    retry: false,
  });
  const batchList = useMemo(() => (bq.data ? normaliseBatches(bq.data) : []), [bq.data]);
  const batch = useMemo(() => {
    if (!m) return null;
    if (m.batch) return m.batch;
    const b = batchOfSlot(batchList, slotName);
    return b ? { id: b.id, label: b.label, slotCount: b.members.length } : null;
  }, [m, batchList, slotName]);
  // the slot sits on a batch's phones but is PENDING / DETACHED there: with #1216 on it engages nobody - said on its own card
  const stuck = useMemo(() => (m && !m.batch ? pendingOfSlot(batchList, slotName) : null), [m, batchList, slotName]);
  const view = useMemo(() => {
    if (!m) return null;
    const sources = sourceRows(m);
    const trend = trendRows(m);
    return { h: headline(m), sources, best: bestOf(sources), ranking: targetRanking(m), trend, spark: heroSpark(trend) };
  }, [m]);

  useEffect(() => {
    if (m && view) {
      fbLog('info', `${FB_TAG} card ${slotName} range ${effective}: ${view.h.state}, engaged ${view.h.engaged}, rate ${fmtRatePct(view.h.rate)}, `
        + `maturing n ${view.h.maturing.engaged}${batch ? `, batch ${batch.id}` : ''}`
        + `${stuck ? `; ${stuck.entry.status} on batch ${stuck.batch.id} (engages nobody in batch mode) - alert shown` : ''}`);
    }
  }, [m, view, slotName, effective, batch, stuck]);

  if (q.isLoading) return null;
  if (q.error) {
    const c = classifyFbError(q.error);
    if (c.kind === 'routeMissing') {
      fbLog('info', `[R16M-UI] Follow-back card not drawn for ${slotName}: no /follow-back route on this server (HTTP ${c.status})`);
      return null;
    }
    if (c.kind === 'notEnabled') {
      fbLog('info', `[R16M-UI] Follow-back card of ${slotName}: switched off on this server (${c.message || 'not_enabled'})`);
      return (
        <CompactLine icon={UserCheck} title="Follow-back" testid="fb-card-off">
          {c.message || 'Follow-back stats are switched off on this server.'}
        </CompactLine>
      );
    }
    fbLog('warn', `[R16M-UI] Follow-back figures of ${slotName} could not be loaded: HTTP ${c.status ?? '-'} ${c.code || ''} ${c.message || ''}`.trim());
    return (
      <CompactLine icon={UserCheck} title="Follow-back" testid="fb-card-error" tone="error">
        {c.kind === 'slotNotFound' ? (c.message || `No slot ${slotName} on this account.`) : `The figures could not be loaded (${r14ErrorText(q.error, 'no answer')}).`}
      </CompactLine>
    );
  }
  if (!m || !view) {
    fbLog('info', `[R16M-UI] Follow-back card not drawn for ${slotName}: the answer carries no follow-back fields`);
    return null;
  }
  const { h } = view;
  const n = slotNumber(slotName);
  const options = rangeOptions(m, [range, 'all'], { short: true }).map((o, i) => (i === 0 ? { ...o, value: 'page', label: `${range} days` } : o));
  return (
    <section className={`space-y-3 transition-opacity ${q.isPlaceholderData ? 'opacity-60' : ''}`} aria-label="Follow-back" data-testid="fb-card"
      data-state={h.state} aria-busy={q.isPlaceholderData || undefined}>
      <div className="aurora-card min-w-0 p-4 sm:p-5">
        <header className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-2 font-heading text-base font-semibold leading-tight text-foreground">
              <UserCheck className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              <span>Follow-back</span>
              <BatchLink batch={batch} testid="fb-card-batch" />
            </h2>
            <p className="mt-1 text-xs text-muted-foreground text-pretty" data-testid="fb-card-subtitle">
              Of the people this slot engaged from its target lists, how many followed it back within {m.windowDays} days, by the source they
              were picked from. Matured periods only: the range ends {m.windowDays} days ago, so everyone counted has had the full
              {' '}{m.windowDays} days; the last {m.windowDays} days are shown apart, as still maturing.
            </p>
          </div>
          <Segmented label="Follow-back range" size="sm" options={options} value={allTime ? 'all' : 'page'}
            onChange={(v) => setAllTime(v === 'all')} testidPrefix="fb-card-range" />
        </header>
        <div className="mt-3 space-y-2">
          <SignalStrip m={m} testid="fb-card-signal" />
          {stuck && (
            <StuckAlert batch={stuck.batch} stuck={[stuck.entry]} slotView testid="fb-card-stuck"
              joinHref={`/follow-back?batch=${encodeURIComponent(stuck.batch.id)}#fb-batch-pending`} />
          )}
          <EmptyNotice m={m} h={h} slotLabel={slotName} testid="fb-card-empty" />
          <NoSignalNote m={m} h={h} testid="fb-card-no-signal" />
        </div>
      </div>
      {h.figures && (
        <>
          <KpiRow m={m} h={h} sources={view.sources} best={view.best} ranking={view.ranking} spark={view.spark} compact />
          <TrendCard m={m} compact withStack testid="fb-card-trend" />
        </>
      )}
      <p className="px-1 text-xs text-muted-foreground">
        <RouterLink to={`/follow-back?slots=${n !== null ? n : encodeURIComponent(slotName)}`} data-testid="fb-card-open-page"
          className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
          By target account, and the batch: open the Follow-back page
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </RouterLink>
      </p>
    </section>
  );
}
